/**
 * iter87.374 (user-requested) — NFL Playoffs Dark Horse Picks. Two
 * separate slots (one per conference, AFC + NFC) — unlike NWSL's
 * single seed-5-8 pick — each restricted to a wild-card team (seed
 * 5-7, i.e. NOT a division winner) in that conference. Points PER WIN
 * (`tournament.scoring.nfl_darkhorse_win_points`, default 4), stacking
 * independently per conference pick.
 *
 * iter87.467 (user-requested — floating Save button on every
 * predictor/Dark Horse modal) — tapping a team used to save
 * instantly; now it only drafts a selection per conference, and a
 * floating "Save Picks" footer button (always visible) commits
 * whichever conference(s) changed.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronRight, Flag, AlertTriangle, Info, Check } from "lucide-react";
import { BentoModal } from "@/components/onui/BentoMotion";
import { TeamSwatch } from "@/components/dashboard/TeamSwatch";

const CONFERENCES = ["AFC", "NFC"];

export default function NflDarkhorsePickModal({ open, onClose, onSaved, tournamentId, participantId, existingPicks, perWinPoints = 4 }) {
    const [savedPicks, setSavedPicks] = useState(existingPicks || {});
    const [draftPicks, setDraftPicks] = useState(existingPicks || {});
    const [bracket, setBracket] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !tournamentId) return;
        setSavedPicks(existingPicks || {});
        setDraftPicks(existingPicks || {});
        api.getNflPoBracket(tournamentId).then(setBracket).catch(() => setBracket(null));
    }, [open, tournamentId, existingPicks]);

    if (!open) return null;
    const allTeams = bracket?.teams || [];
    const locked = !!bracket?.pre_season_locked;
    // iter87.444 — mirrors NflChampionPickModal.jsx's seeding-not-final banner.
    const fieldFinal = (bracket?.rounds || []).some((r) => (r.games || []).length > 0);
    const dirtyConfs = CONFERENCES.filter((c) => draftPicks[c] !== savedPicks[c]);
    const savedCount = CONFERENCES.filter((c) => savedPicks[c]).length;

    const onSavePicks = async () => {
        if (locked || dirtyConfs.length === 0) return;
        setSaving(true);
        try {
            await Promise.all(dirtyConfs.map((c) => api.submitNflDarkhorsePick(tournamentId, participantId, c, draftPicks[c])));
            setSavedPicks({ ...savedPicks, ...Object.fromEntries(dirtyConfs.map((c) => [c, draftPicks[c]])) });
            onSaved?.(draftPicks);
            toast.success("Dark Horse picks saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save your picks");
        } finally {
            setSaving(false);
        }
    };

    return (
        <BentoModal
            open={open}
            onClose={onClose}
            testId="nfl-darkhorse-pick-modal"
            backdropClassName="lg:items-center lg:justify-center lg:p-4"
            panelClassName="rounded-2xl border border-heat/40 bg-obsidian max-w-lg w-full max-h-[90dvh] overflow-hidden flex flex-col"
        >
            <div className="flex items-center justify-between gap-2 px-4 sm:px-6 pt-4 sm:pt-6 pb-3 border-b border-white/[0.06] shrink-0">
                <div className="min-w-0 flex items-center gap-2 flex-1">
                    <div className="w-9 h-9 rounded-xl bg-heat flex items-center justify-center shrink-0">
                        <Flag className="w-4 h-4 text-black" strokeWidth={2.5} />
                    </div>
                    <h3 className="font-display font-extrabold text-lg uppercase truncate">Dark Horse Picks</h3>
                </div>
                <button
                    onClick={onClose}
                    className="w-10 h-10 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center transition-colors active:scale-95 shrink-0"
                    data-testid="close-nfl-darkhorse-pick-modal-btn"
                    aria-label="Close"
                >
                    <ChevronRight className="w-4 h-4 rotate-90" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">

            <div className="text-[11px] text-zinc-500 mb-4 flex gap-2 items-start text-left" data-testid="nfl-darkhorse-pick-explainer">
                <Info className="w-4 h-4 text-cyber shrink-0 mt-0.5" />
                <p className="leading-relaxed">
                    <span className="text-white font-bold uppercase tracking-tight">How it works:</span>{" "}
                    Pick 1 wild-card team (seed 5-7 — not a division winner) per conference. +{perWinPoints} pts for every playoff game that team wins, stacking across rounds. Locks the instant Wild Card Weekend kicks off.
                </p>
            </div>

            {locked && (
                <p data-testid="nfl-darkhorse-locked-notice" className="text-heat text-[11px] uppercase tracking-widest font-bold mb-3">
                    Locked — Wild Card Weekend has started. Your picks below are final.
                </p>
            )}

            {!locked && !fieldFinal && allTeams.length > 0 && (
                <div
                    data-testid="nfl-darkhorse-seeding-not-final-banner"
                    className="text-volt text-[11px] font-semibold flex items-start gap-1.5 bg-volt/10 border border-volt/40 rounded-lg px-3 py-2 mb-3"
                >
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                        Seeding isn't final yet — this field is projected only and can still shift before the
                        last day of the regular season. Come back to make your final pick once the season ends
                        and seeding is official.
                    </span>
                </div>
            )}

            {CONFERENCES.map((conf) => {
                const eligible = allTeams.filter((t) => t.conference === conf && !t.missed_playoffs && Number(t.seed) >= 5).sort((a, b) => a.seed - b.seed);
                const draftId = draftPicks[conf];
                const savedTeam = savedPicks[conf] ? eligible.find((t) => t.id === savedPicks[conf]) : null;
                return (
                    <div key={conf} className="mb-4" data-testid={`nfl-darkhorse-slot-${conf}`}>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">
                            {conf} Dark Horse — {savedTeam ? <span className="text-heat">Saved: {savedTeam.name}</span> : "no pick saved"}
                        </p>
                        {eligible.length === 0 ? (
                            <p className="text-xs text-zinc-500 rounded-lg border border-white/[0.08] bg-white/[0.02] p-3" data-testid={`nfl-darkhorse-no-teams-${conf}`}>
                                No wild-card seeds set for the {conf} yet.
                            </p>
                        ) : (
                            <div className="grid grid-cols-3 gap-2">
                                {eligible.map((t) => {
                                    const isSelected = draftId === t.id;
                                    return (
                                        <motion.button
                                            key={t.id}
                                            data-testid={`nfl-darkhorse-option-${conf}-${t.id}`}
                                            disabled={locked}
                                            onClick={() => !locked && setDraftPicks((p) => ({ ...p, [conf]: t.id }))}
                                            whileHover={!locked ? { y: -2, scale: 1.015 } : {}}
                                            whileTap={!locked ? { scale: 0.975 } : {}}
                                            transition={{ type: "spring", stiffness: 400, damping: 30 }}
                                            className={`p-2.5 rounded-lg border text-left disabled:opacity-40 disabled:cursor-not-allowed ${
                                                isSelected
                                                    ? "border-heat bg-heat/[0.08]"
                                                    : "border-white/[0.08] bg-[#141414] hover:bg-[#181818] hover:border-heat/40"
                                            }`}
                                        >
                                            <TeamSwatch name={t.name} sport="nfl" size="w-7 h-7 text-[9px]" />
                                            <div className="text-xs font-bold truncate mt-1.5 flex items-center gap-1">{t.name}{isSelected && <Check className="w-3 h-3 text-heat shrink-0" />}</div>
                                            <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold mt-0.5">Seed #{t.seed}</div>
                                        </motion.button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}

            </div>

            <div className="shrink-0 glass border-t border-white/10 px-4 sm:px-6 py-3" data-testid="nfl-darkhorse-pick-footer">
                <div className="flex items-center justify-between gap-3">
                    <span className="text-[11px] uppercase tracking-widest font-bold text-zinc-400" data-testid="nfl-darkhorse-pick-count">
                        {dirtyConfs.length > 0 ? `${dirtyConfs.length} unsaved` : `${savedCount}/2 saved`}
                    </span>
                    <button
                        type="button"
                        disabled={saving || dirtyConfs.length === 0 || locked}
                        onClick={onSavePicks}
                        data-testid="nfl-darkhorse-save-btn"
                        className="shrink-0 inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-black bg-heat text-black font-display font-extrabold uppercase tracking-wide text-xs px-4 min-h-[44px] transition-all duration-150 active:scale-95 disabled:opacity-40 disabled:pointer-events-none"
                    >
                        <Check className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save Picks"}
                    </button>
                </div>
            </div>
        </BentoModal>
    );
}
